import React, { useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { EQUIPMENT_LABELS, LABEL_METRICS, LABEL_Y_OFFSETS } from './equipmentDefs';
import {
    LABEL_ACCENT, LABEL_TEXT, LABEL_DIM, LABEL_BG, LABEL_BG_LIGHT, STATUS_ON, STATUS_OFF,
    SOLAR_PRIMARY, BATTERY_PRIMARY, EV_PRIMARY, COLD_PRIMARY,
    GRID_IMPORT, GRID_EXPORT,
} from './twinColors';
import { useThemeMode } from '@vortx/shared-ui';

/**
 * Format a metric value for display.
 */
function formatValue(raw, metric) {
    if (raw == null) return '--';
    if (metric.format) return metric.format(raw);
    return typeof raw === 'number' ? raw.toFixed(0) : String(raw);
}

/** Equipment-specific accent color. */
const EQUIPMENT_COLORS = {
    solar: SOLAR_PRIMARY,
    battery: BATTERY_PRIMARY,
    ev_fleet: EV_PRIMARY,
    cold_storage: COLD_PRIMARY,
    grid: GRID_IMPORT,
    building_load: LABEL_TEXT,
};

function getMetricColor(equipmentType, key, value) {
    if (key === 'power_kw' && equipmentType === 'grid') {
        return value > 0 ? GRID_IMPORT : GRID_EXPORT;
    }
    return EQUIPMENT_COLORS[equipmentType] || LABEL_TEXT;
}

function getStatusDotColor(equipmentType, state) {
    if (!state) return STATUS_OFF;
    const power = state.power_kw;
    if (power == null || power === 0) return STATUS_OFF;
    return EQUIPMENT_COLORS[equipmentType] || STATUS_ON;
}

/**
 * EquipmentLabel - Floating label above each equipment asset in the 3D scene.
 * Adapted for holographic point cloud aesthetic with deep blue palette.
 *
 * @param {{
 *   equipmentType: string,
 *   state: object,
 *   position: number[],
 * }} props
 */
export default function EquipmentLabel({ equipmentType, state, position }) {
    const { isRetro } = useThemeMode();
    const bgColor = isRetro ? LABEL_BG : LABEL_BG_LIGHT;
    const label = EQUIPMENT_LABELS[equipmentType] || equipmentType.toUpperCase();
    const metrics = LABEL_METRICS[equipmentType] || [];
    const yOffset = LABEL_Y_OFFSETS[equipmentType] || 5;

    const labelPos = useMemo(
        () => [position[0], position[1] + yOffset, position[2]],
        [position, yOffset]
    );

    const statusColor = useMemo(
        () => getStatusDotColor(equipmentType, state),
        [equipmentType, state]
    );

    const primaryMetric = metrics[0] || null;
    const secondaryMetric = metrics[1] || null;

    const primaryRaw = primaryMetric ? state?.[primaryMetric.key] : null;
    const primaryDisplay = primaryMetric
        ? `${formatValue(primaryRaw, primaryMetric)} ${primaryMetric.unit}`
        : '';
    const primaryColor = primaryMetric
        ? getMetricColor(equipmentType, primaryMetric.key, primaryRaw)
        : LABEL_TEXT;

    const secondaryRaw = secondaryMetric ? state?.[secondaryMetric.key] : null;
    const secondaryDisplay = secondaryMetric
        ? `${formatValue(secondaryRaw, secondaryMetric)} ${secondaryMetric.unit}`
        : '';
    const secondaryColor = secondaryMetric
        ? getMetricColor(equipmentType, secondaryMetric.key, secondaryRaw)
        : LABEL_TEXT;

    return (
        <Billboard
            follow={true}
            lockX={false}
            lockY={false}
            lockZ={false}
            position={labelPos}
        >
            {/* Dark backing panel — near-black with slight blue tint */}
            <mesh position={[0, 0, 0]}>
                <planeGeometry args={[6, 2.5]} />
                <meshBasicMaterial
                    color={bgColor}
                    opacity={0.85}
                    transparent={true}
                    depthWrite={false}
                    toneMapped={false}
                />
            </mesh>

            {/* Status dot */}
            <mesh position={[-2.5, 0.7, 0.01]}>
                <circleGeometry args={[0.12, 16]} />
                <meshBasicMaterial
                    color={statusColor}
                    toneMapped={false}
                />
            </mesh>

            {/* Equipment name */}
            <Text
                fontSize={0.35}
                color={LABEL_ACCENT}
                anchorX="left"
                anchorY="middle"
                position={[-2.2, 0.7, 0.01]}
                fontWeight="bold"
                letterSpacing={0.08}
                toneMapped={false}
            >
                {label}
            </Text>

            {/* Primary metric value */}
            {primaryMetric && (
                <Text
                    fontSize={0.6}
                    fontWeight="bold"
                    color={primaryColor}
                    anchorX="center"
                    anchorY="middle"
                    position={[0, 0, 0.01]}
                    toneMapped={false}
                >
                    {primaryDisplay}
                </Text>
            )}

            {/* Primary metric label */}
            {primaryMetric && (
                <Text
                    fontSize={0.25}
                    color={LABEL_DIM}
                    anchorX="center"
                    anchorY="middle"
                    position={[0, -0.5, 0.01]}
                    toneMapped={false}
                >
                    {primaryMetric.label}
                </Text>
            )}

            {/* Secondary metric */}
            {secondaryMetric && (
                <Text
                    fontSize={0.3}
                    color={secondaryColor}
                    anchorX="center"
                    anchorY="middle"
                    position={[0, -0.9, 0.01]}
                    toneMapped={false}
                >
                    {secondaryDisplay}
                </Text>
            )}
        </Billboard>
    );
}
